import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaClock, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';

const ActiveOrders = () => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveOrders();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActiveOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/api/offers/buyer/offers?role=buyer', {
        headers: { Authorization: `Bearer ${token}` }
      });

      let orders = response.data?.data || [];
      // Filter orders that are active (not completed or rejected)
      const active = orders.filter(order => 
        order.status === 'accepted' || 
        order.status === 'in_progress' || 
        order.status === 'delivered'
      );
      
      setActiveOrders(active);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching active orders:', error);
      setLoading(false);
    }
  };

  const getTimeLeft = (order) => {
    if (order.status !== 'in_progress' || !order.startedAt) return null;
    
    const deliveryDays = order.deliveryTime || order.timeline || 7;
    const started = new Date(order.startedAt);
    const deadline = new Date(started);
    deadline.setDate(deadline.getDate() + deliveryDays);
    const now = new Date();
    const diff = deadline - now;
    
    if (diff <= 0) return { expired: true };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (86400000)) / (3600000));
    return { days, hours, expired: false };
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'accepted': return { text: 'Waiting for Seller', color: 'bg-yellow-500' };
      case 'in_progress': return { text: 'In Progress', color: 'bg-blue-500' };
      case 'delivered': return { text: 'Delivered - Ready to Review', color: 'bg-purple-500' };
      default: return { text: status, color: 'bg-gray-500' };
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-gray-500">Loading active orders...</div>;
  }

  if (activeOrders.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        No active orders. Browse services to get started.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-3">Active Orders</h3>
      {activeOrders.map((order) => {
        const timeLeft = getTimeLeft(order);
        const statusBadge = getStatusBadge(order.status);
        const price = order.price || order.amount || 0;
        
        return (
          <div key={order._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-lg">{order.title}</h4>
                <p className="text-gray-600 text-sm mt-1">Order #{order._id.slice(-8)}</p>
              </div>
              <span className={`${statusBadge.color} text-white px-3 py-1 rounded-full text-xs font-semibold`}>
                {statusBadge.text}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <p className="text-gray-500 text-xs">Amount</p>
                <p className="font-bold text-lg">${price}</p>
              </div>
              {timeLeft && !timeLeft.expired && order.status === 'in_progress' && (
                <div>
                  <p className="text-gray-500 text-xs flex items-center gap-1">
                    <FaClock className="text-xs" /> Time Remaining
                  </p>
                  <p className="font-semibold text-orange-600">
                    {timeLeft.days}d {timeLeft.hours}h
                  </p>
                </div>
              )}
              {timeLeft?.expired && order.status === 'in_progress' && (
                <div>
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <FaHourglassHalf /> Overdue
                  </p>
                </div>
              )}
              {order.status === 'delivered' && (
                <div>
                  <p className="text-green-500 text-xs flex items-center gap-1">
                    <FaCheckCircle /> Awaiting Your Approval
                  </p>
                </div>
              )}
            </div>
            
            <button
              onClick={() => navigate(`/buyer/order/${order._id}`)}
              className="w-full bg-[#708238] text-white py-2 rounded-lg hover:bg-[#5a6a2d] transition text-sm font-semibold"
            >
              {order.status === 'delivered' ? 'Review Delivery →' : 'View Order Details →'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ActiveOrders;
